import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    selectFullReport,
    selectHourlyReport,
    selectTaxwiseReport,
    selectPerformanceReport,
    selectFromDate,
    selectToDate,
    selectSelectedBranch,
    selectActiveTab,
    selectActiveLoading,
    selectActiveError,
    setActiveTab,
    fetchItemFullReport,
    fetchItemHourlyReport,
    fetchItemTaxwiseReport,
    fetchItemPerformanceReport,
    ITEM_REPORT_TABS
} from '../../store/slices/itemReportSlice';
import ReportSummaryWithChart from './ReportSummaryWithChart';
import { exportStyledExcel, exportStyledExcelMultiSheet, buildReportFilterLines } from '../../utilities/excelExport';
import './billreports.css';

const getBranchId = (selectedBranch) => {
    const b = selectedBranch;
    return (b && (typeof b === 'object' ? b.branch_id : b)) || 'All';
};

const formatCurrency = (amount) => {
    const num = parseFloat(amount || 0);
    return num.toLocaleString('en-IN', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
    });
};

const parseNumber = (value) => parseFloat(value || 0) || 0;

const EmptyState = ({ message = 'No data available. Apply filters and click Apply to load.' }) => (
    <div className="flex flex-column align-items-center justify-content-center p-6">
        <i className="pi pi-inbox text-6xl text-color-secondary mb-3" />
        <p className="text-xl text-color-secondary">No item data available</p>
        <p className="text-color-secondary mt-2">{message}</p>
    </div>
);

const FullReportTab = ({ fullReport, fromDate, toDate, selectedBranch, isManagerView = false }) => {
    const { bills = [] } = fullReport;
    const hasData = Array.isArray(bills) && bills.length > 0;
    const columnsForExport = [
        { key: 'invoice_date', header: 'Invoice Date', getValue: (r) => r.invoice_date || '-' },
        { key: 'product_name', header: 'Product Name', getValue: (r) => r.product_name || '-' },
        { key: 'category_name', header: 'Category', getValue: (r) => r.category_name || '-' },
        { key: 'hsn_code', header: 'HSN Code', getValue: (r) => r.hsn_code || '-' },
        { key: 'uom', header: 'UOM', getValue: (r) => r.uom || '-' },
        { key: 'basic_rate', header: 'Basic Rate', getValue: (r) => parseNumber(r.basic_rate), numeric: true, skipSum: true },
        { key: 'tax_percentage', header: 'Tax %', getValue: (r) => parseNumber(r.tax_percentage), numeric: true, skipSum: true },
        { key: 'cgst_percentage', header: 'CGST %', getValue: (r) => parseNumber(r.cgst_percentage), numeric: true, skipSum: true },
        { key: 'sgst_percentage', header: 'SGST %', getValue: (r) => parseNumber(r.sgst_percentage), numeric: true, skipSum: true },
        { key: 'total_qty', header: 'Total Qty', getValue: (r) => parseNumber(r.total_qty), numeric: true },
        { key: 'discount_amount', header: 'Discount Amount', getValue: (r) => parseNumber(r.discount_amount), numeric: true },
        { key: 'taxable_amount', header: 'Taxable Amount', getValue: (r) => parseNumber(r.taxable_amount), numeric: true },
        { key: 'tax_amount', header: 'Tax Amount', getValue: (r) => parseNumber(r.tax_amount), numeric: true },
        { key: 'cgst_amount', header: 'CGST Amount', getValue: (r) => parseNumber(r.cgst_amount), numeric: true },
        { key: 'sgst_amount', header: 'SGST Amount', getValue: (r) => parseNumber(r.sgst_amount), numeric: true },
        { key: 'total_amount', header: 'Total Amount', getValue: (r) => parseNumber(r.total_amount), numeric: true }
    ];

    if (!hasData) {
        return (
            <Card>
                <EmptyState message="Item full report will show here once data is loaded (select dates, branch and apply, or click this tab to load)." />
            </Card>
        );
    }

    const isZeroLike = (value) => {
        if (value === null || value === undefined) return false;
        const n = parseFloat(String(value).trim());
        return Number.isFinite(n) && n === 0;
    };

    const formatHeaderFromKey = (key) =>
        String(key || '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (ch) => ch.toUpperCase());

    const allKeys = bills.length
        ? [
              ...Object.keys(bills[0]),
              ...Array.from(new Set(bills.slice(1).flatMap((row) => Object.keys(row || {})))).filter((k) => !(k in bills[0]))
          ]
        : [];

    const isNumericColumn = (field) => {
        const key = String(field || '').toLowerCase();
        if (key.includes('code') || key.includes('date')) return false;
        const values = bills.map((row) => row?.[field]).filter((v) => v !== null && v !== undefined && String(v).trim() !== '');
        if (!values.length) return false;
        return values.every((v) => /^-?\d+(\.\d+)?$/.test(String(v).trim()));
    };

    const isCurrencyLikeField = (field) => {
        const key = String(field || '').toLowerCase();
        const strictNonCurrencyHints = ['percent', 'percentage', 'cgst', 'sgst', 'gst', 'quantity', 'qty'];
        if (strictNonCurrencyHints.some((hint) => key.includes(hint))) return false;

        // Amount keys should always be treated as currency (e.g. taxable_amt).
        if (key.includes('amount') || key.includes('amt')) return true;

        if (key.includes('tax')) return false;

        const currencyHints = ['rate', 'price', 'revenue', 'value', 'sale'];
        return currencyHints.some((hint) => key.includes(hint));
    };

    const tableColumns = allKeys.map((field) => {
        const numeric = isNumericColumn(field);
        const currencyLike = numeric && isCurrencyLikeField(field);
        return {
            field,
            sortable: true,
            style: { textAlign: numeric ? 'right' : 'left' },
            body: (row) => {
                const value = row?.[field];
                if (value === null || value === undefined || value === '') return '-';
                if (numeric) {
                    const formatted = parseNumber(value).toLocaleString(undefined, { minimumFractionDigits: 2 });
                    return currencyLike ? `₹${formatted}` : formatted;
                }
                if (String(field).toLowerCase().includes('date') && /^\d{4}-\d{2}-\d{2}/.test(String(value))) {
                    const [y, m, d] = String(value).split('-');
                    return `${d}-${m}-${y}`;
                }
                return String(value);
            },
            hideIfAllZero: numeric,
            isCurrencyLike: currencyLike
        };
    });

    const visibleTableColumns = tableColumns.filter((col) => {
        if (!col.hideIfAllZero) return true;
        return !bills.every((row) => isZeroLike(col.valueGetter ? col.valueGetter(row) : row?.[col.field]));
    });

    const footerByField = visibleTableColumns.reduce((acc, col) => {
        if (!col.hideIfAllZero) return acc;
        const footerDisabledHints = ['tax_percentage', 'cgst_tax', 'sgst_tax'];
        if (footerDisabledHints.some((hint) => String(col.field).toLowerCase().includes(hint))) {
            acc[col.field] = '';
            return acc;
        }
        const sum = bills.reduce((total, row) => total + parseNumber(row?.[col.field]), 0);
        const formatted = sum.toLocaleString(undefined, { minimumFractionDigits: 2 });
        acc[col.field] = col.isCurrencyLike ? `₹${formatted}` : formatted;
        return acc;
    }, {});

    const footerCellStyle = { textAlign: 'center', fontWeight: '700', color: 'var(--primary-color)' };
    const footerGroup = (
        <ColumnGroup>
            <Row>
                {visibleTableColumns.map((col, index) => (
                    <Column key={col.field} footer={index === 0 ? 'Total' : footerByField[col.field] || ''} style={footerCellStyle} />
                ))}
            </Row>
        </ColumnGroup>
    );

    const exportToExcel = async () => {
        if (!hasData) return;
        await exportStyledExcel({
            fileName: `ItemFullReport_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheetName: 'Item Full Report',
            title: 'Item Full Report (Date Range)',
            filterLines: buildReportFilterLines({ fromDate, toDate, selectedBranch }),
            columns: columnsForExport,
            rows: bills
        });
    };

    const exportToPDF = () => {
        if (!hasData) return;
        const doc = new jsPDF('l', 'mm', 'a4');
        doc.setFontSize(14);
        doc.text('Item Full Report (Date Range)', 14, 15);
        doc.setFontSize(9);
        let y = 23;
        if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, y);
            y += 5;
        }
        if (selectedBranch && (selectedBranch.branch_name || selectedBranch)) {
            doc.text(`Branch: ${selectedBranch.branch_name || selectedBranch}`, 14, y);
            y += 5;
        }
        autoTable(doc, {
            startY: y + 2,
            head: [columnsForExport.map((c) => c.header)],
            body: bills.map((row) =>
                columnsForExport.map((col) => {
                    const value = col.getValue(row);
                    return typeof value === 'number' ? value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(value ?? '');
                })
            ),
            styles: { fontSize: 7 },
            headStyles: { fillColor: [0, 122, 255] }
        });
        doc.save(`ItemFullReport_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    return (
        <div className="bill-reports-container">
            <Card>
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <h3 className="text-xl font-semibold m-0">Full Report (Date Range)</h3>
                    <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                        {(fromDate || toDate) && (
                            <span className="text-sm text-color-secondary">
                                {fromDate && toDate
                                    ? `${typeof fromDate === 'object' && fromDate.toLocaleDateString ? fromDate.toLocaleDateString('en-IN') : fromDate} – ${
                                          typeof toDate === 'object' && toDate.toLocaleDateString ? toDate.toLocaleDateString('en-IN') : toDate
                                      }`
                                    : ''}
                                {selectedBranch && (selectedBranch.branch_name || selectedBranch) && ` • ${selectedBranch.branch_name || selectedBranch}`}
                            </span>
                        )}
                        {!isManagerView && <Button label="Export Excel" icon="pi pi-file-excel" className="p-button-success p-button-sm" onClick={exportToExcel} disabled={!hasData} />}
                        {!isManagerView && <Button label="Export PDF" icon="pi pi-file-pdf" className="p-button-danger p-button-sm" onClick={exportToPDF} disabled={!hasData} />}
                    </div>
                </div>
                <DataTable
                    value={bills}
                    dataKey="product_name"
                    showGridlines
                    paginator
                    rows={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    tableStyle={{ width: '100%', tableLayout: 'auto' }}
                    className="p-datatable-sm"
                    footerColumnGroup={isManagerView ? null : footerGroup}
                >
                    {visibleTableColumns.map((col) => (
                        <Column key={col.field} field={col.field} header={formatHeaderFromKey(col.field)} body={col.body} style={col.style} sortable={col.sortable} />
                    ))}
                </DataTable>
            </Card>
        </div>
    );
};

const getItemLabelFromRows = (hourlyReport, key) => {
    const val = hourlyReport[key];
    const rows = Array.isArray(val) ? val : val && typeof val === 'object' && Array.isArray(val.rows) ? val.rows : [];
    const first = rows[0];
    if (first && (first.product_name || first.item_name || first.name || first.productname)) {
        return first.product_name || first.item_name || first.name || first.productname;
    }
    return null;
};

const HourlyReportTab = ({ hourlyReport, fromDate, toDate, selectedBranch, isManagerView = false }) => {
    const [itemSearch, setItemSearch] = useState('');

    const isItemWithHourlyDataStructure = (arr) => Array.isArray(arr) && arr.length > 0 && arr.some((item) => Array.isArray(item?.hourly_data) && item.hourly_data.length > 0);
    const [itemSortOrder, setItemSortOrder] = useState(1); // 1 = A→Z, -1 = Z→A
    const [selectedItems, setSelectedItems] = useState([]); // multiple selection, order = pin-to-top order

    const itemKeys = useMemo(() => {
        if (!hourlyReport || typeof hourlyReport !== 'object') return [];
        if (Array.isArray(hourlyReport)) {
            if (isItemWithHourlyDataStructure(hourlyReport)) {
                return hourlyReport.filter((item) => Array.isArray(item.hourly_data) && item.hourly_data.length > 0).map((item) => item.product_name || item.item_name || item.name || item.productname || 'Unknown');
            }
            const byProduct = {};
            hourlyReport.forEach((row) => {
                const name = row.product_name || row.item_name || row.name || row.productname || 'Unknown';
                if (!byProduct[name]) byProduct[name] = [];
                byProduct[name].push(row);
            });
            return Object.keys(byProduct).filter((name) => byProduct[name].length > 0);
        }
        return Object.keys(hourlyReport).filter((key) => {
            const val = hourlyReport[key];
            return Array.isArray(val) ? val.length > 0 : val && typeof val === 'object';
        });
    }, [hourlyReport]);

    const getTimeRangeFromRow = (row) => row.time_range ?? row.Time_Range ?? row.time_slot ?? row.hour_range ?? row.time_range_label ?? '';

    const normalizedHourlyByItem = useMemo(() => {
        if (!hourlyReport || typeof hourlyReport !== 'object') return {};
        const normalizeRow = (row) => ({
            ...row,
            time_range: getTimeRangeFromRow(row)
        });
        if (Array.isArray(hourlyReport)) {
            const byProduct = {};
            if (isItemWithHourlyDataStructure(hourlyReport)) {
                hourlyReport.forEach((item) => {
                    const name = item.product_name || item.item_name || item.name || item.productname || 'Unknown';
                    const slots = Array.isArray(item.hourly_data) ? item.hourly_data : [];
                    if (slots.length === 0) return;
                    if (!byProduct[name]) byProduct[name] = [];
                    byProduct[name].push(...slots.map((row) => normalizeRow(row)));
                });
                return byProduct;
            }
            hourlyReport.forEach((row) => {
                const name = row.product_name || row.item_name || row.name || row.productname || 'Unknown';
                if (!byProduct[name]) byProduct[name] = [];
                byProduct[name].push(normalizeRow(row));
            });
            return byProduct;
        }
        const numericKeys = itemKeys.every((k) => /^\d+$/.test(k));
        if (numericKeys && itemKeys.length > 0) {
            const out = {};
            itemKeys.forEach((key) => {
                const label = getItemLabelFromRows(hourlyReport, key);
                const rows = Array.isArray(hourlyReport[key]) ? hourlyReport[key] : [];
                const normalized = rows.map(normalizeRow);
                if (label) out[label] = normalized;
                else out[`Item ${key}`] = normalized;
            });
            return out;
        }
        const out = {};
        Object.keys(hourlyReport).forEach((key) => {
            const val = hourlyReport[key];
            const rows = Array.isArray(val) ? val : val?.rows ?? [];
            if (rows.length) out[key] = rows.map(normalizeRow);
        });
        return Object.keys(out).length ? out : hourlyReport;
    }, [hourlyReport, itemKeys]);

    const itemNames = useMemo(() => {
        const names = Object.keys(normalizedHourlyByItem).sort((a, b) => a.toString().localeCompare(b.toString(), undefined, { sensitivity: 'base' }));
        return itemSortOrder === 1 ? names : names.slice().reverse();
    }, [normalizedHourlyByItem, itemSortOrder]);

    const filteredItemNames = useMemo(() => {
        if (!itemSearch) return itemNames;
        return itemNames.filter((name) => name.toLowerCase().includes(itemSearch.toLowerCase()));
    }, [itemNames, itemSearch]);

    const getTimeSortKey = (label) => {
        if (!label || typeof label !== 'string') return Number.MAX_SAFE_INTEGER;
        const [startPart] = label.split('-');
        const part = (startPart || '').trim().toLowerCase();
        const match = part.match(/(\d{1,2}):(\d{2})\s*(am|pm)/i);
        if (!match) return Number.MAX_SAFE_INTEGER - 1;
        let hour = parseInt(match[1], 10);
        const minute = parseInt(match[2], 10) || 0;
        const meridiem = match[3];
        if (meridiem === 'pm' && hour < 12) hour += 12;
        if (meridiem === 'am' && hour === 12) hour = 0;
        return hour * 60 + minute;
    };

    const allTimeRanges = useMemo(() => {
        const set = new Set();
        Object.values(normalizedHourlyByItem).forEach((rows) => {
            (rows || []).forEach((row) => {
                const tr = row.time_range;
                if (tr) set.add(tr);
            });
        });
        return Array.from(set).sort((a, b) => getTimeSortKey(a) - getTimeSortKey(b));
    }, [normalizedHourlyByItem]);

    const selectedTotalByTimeRange = useMemo(() => {
        const totals = {};
        allTimeRanges.forEach((tr) => {
            totals[tr] = { sales: 0, qty: 0 };
        });
        selectedItems.forEach((name) => {
            const rows = normalizedHourlyByItem[name] || [];
            rows.forEach((row) => {
                if (row.time_range && totals[row.time_range]) {
                    totals[row.time_range].sales += parseFloat(row.total_amount || 0);
                    totals[row.time_range].qty += parseInt(row.total_qty || 0, 10) || 0;
                }
            });
        });
        return totals;
    }, [selectedItems, normalizedHourlyByItem, allTimeRanges]);

    const pivotRows = useMemo(() => {
        const selectedOrdered = selectedItems.filter((n) => filteredItemNames.includes(n));
        const rest = filteredItemNames.filter((n) => !selectedItems.includes(n));
        const orderedNames = [...selectedOrdered, ...rest];
        const dataRows = orderedNames.map((name) => {
            const base = { item_name: name };
            const rows = normalizedHourlyByItem[name] || [];
            rows.forEach((row) => {
                if (row.time_range) {
                    base[row.time_range] = row;
                }
            });
            return base;
        });
        if (selectedItems.length === 0) return dataRows;
        const summaryRow = {
            item_name: 'Total (Selected)',
            _isSummaryRow: true
        };
        allTimeRanges.forEach((tr) => {
            summaryRow[tr] = {
                total_amount: selectedTotalByTimeRange[tr]?.sales ?? 0,
                total_qty: selectedTotalByTimeRange[tr]?.qty ?? 0
            };
        });
        return [summaryRow, ...dataRows];
    }, [filteredItemNames, normalizedHourlyByItem, selectedItems, selectedTotalByTimeRange, allTimeRanges]);

    const grandTotalByTimeRange = useMemo(() => {
        const totals = {};
        allTimeRanges.forEach((tr) => {
            totals[tr] = { sales: 0, qty: 0 };
        });
        pivotRows
            .filter((row) => !row._isSummaryRow)
            .forEach((row) => {
                allTimeRanges.forEach((tr) => {
                    const slot = row[tr];
                    if (slot) {
                        totals[tr].sales += parseFloat(slot.total_amount || 0);
                        totals[tr].qty += parseInt(slot.total_qty || 0, 10) || 0;
                    }
                });
            });
        return totals;
    }, [pivotRows, allTimeRanges]);

    const hasData = itemNames.length > 0 && allTimeRanges.length > 0;

    const itemHeaderTemplate = (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => setItemSortOrder((prev) => (prev === 1 ? -1 : 1))}>
            <span>Item</span>
            <i className={`pi ${itemSortOrder === 1 ? 'pi-sort-alpha-down' : 'pi-sort-alpha-up'}`} />
        </span>
    );

    const renderTimeCell = (slotRow) => {
        if (!slotRow) return '-';
        const sales = `₹${formatCurrency(slotRow.total_amount)}`;
        const qty = slotRow.total_qty ?? 0;
        const split = slotRow.splitted || {};
        const tooltipParts = [
            'Split details for this time slot:',
            `NC Qty: ${split.nc_qty ?? 0}`,
            `NC Sales: ₹${formatCurrency(split.nc_sales)}`,
            `Discount Qty: ${split.discount_qty ?? 0}`,
            `Discount Sales: ₹${formatCurrency(split.discount_sales)}`,
            `Actual Qty: ${split.actual_qty ?? 0}`,
            `Actual Sales: ₹${formatCurrency(split.Actival_Sales)}`
        ];
        const tooltip = tooltipParts.join('\n');
        return (
            <div className="hourly-slot-cell" data-pr-tooltip={tooltip} data-pr-position="top" style={{ lineHeight: 1.3, cursor: 'pointer' }}>
                <div>{sales}</div>
                <div style={{ fontSize: '0.8rem' }}>Qty: {qty}</div>
            </div>
        );
    };

    const exportToExcel = async () => {
        if (!hasData) return;
        const rows = pivotRows.filter((row) => !row._isSummaryRow);
        const columns = [
            { key: 'item_name', header: 'Item', getValue: (r) => r.item_name || '-' },
            ...allTimeRanges.flatMap((tr) => [
                { key: `${tr}_sales`, header: `${tr} Sales`, getValue: (r) => parseNumber(r[tr]?.total_amount), numeric: true },
                { key: `${tr}_qty`, header: `${tr} Qty`, getValue: (r) => parseNumber(r[tr]?.total_qty), numeric: true }
            ])
        ];
        await exportStyledExcel({
            fileName: `ItemHourlyReport_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheetName: 'Item Hourly Report',
            title: 'Item Hourly Report',
            filterLines: buildReportFilterLines({ fromDate, toDate, selectedBranch }),
            columns,
            rows
        });
    };

    const exportToPDF = () => {
        if (!hasData) return;
        const doc = new jsPDF('l', 'mm', 'a3');
        doc.setFontSize(14);
        doc.text('Item Hourly Report', 14, 15);
        doc.setFontSize(9);
        let y = 23;
        if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, y);
            y += 5;
        }
        if (selectedBranch && (selectedBranch.branch_name || selectedBranch)) {
            doc.text(`Branch: ${selectedBranch.branch_name || selectedBranch}`, 14, y);
            y += 5;
        }
        const headers = ['Item'];
        allTimeRanges.forEach((tr) => {
            headers.push(`${tr} Sales`);
            headers.push(`${tr} Qty`);
        });
        const body = pivotRows
            .filter((row) => !row._isSummaryRow)
            .map((row) => {
                const rowValues = [row.item_name];
                allTimeRanges.forEach((tr) => {
                    rowValues.push(formatCurrency(row[tr]?.total_amount));
                    rowValues.push(parseNumber(row[tr]?.total_qty).toString());
                });
                return rowValues;
            });
        autoTable(doc, {
            startY: y + 2,
            head: [headers],
            body,
            styles: { fontSize: 6 },
            headStyles: { fillColor: [0, 122, 255] }
        });
        doc.save(`ItemHourlyReport_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    if (!hasData) {
        return (
            <Card>
                <EmptyState message="Hourly report will show here once data is loaded. Select Hourly Sales tab, apply filters and click Apply." />
            </Card>
        );
    }

    return (
        <div className="bill-reports-container">
            <Card>
                <Tooltip key={`${itemSearch}-${pivotRows.length}-${itemSortOrder}-${selectedItems.length}`} target=".hourly-slot-cell" />
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <h3 className="text-xl font-semibold m-0">Item-wise hourly sales</h3>
                    <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                        {hasData && <Tag value={`${itemNames.length} items • ${allTimeRanges.length} time ranges`} severity="info" />}
                        {(fromDate || toDate) && (
                            <span className="text-sm text-color-secondary">
                                {fromDate && toDate
                                    ? `${typeof fromDate === 'object' && fromDate.toLocaleDateString ? fromDate.toLocaleDateString('en-IN') : fromDate} – ${
                                          typeof toDate === 'object' && toDate.toLocaleDateString ? toDate.toLocaleDateString('en-IN') : toDate
                                      }`
                                    : ''}
                                {selectedBranch?.branch_name ? ` • ${selectedBranch.branch_name}` : ''}
                            </span>
                        )}
                        {!isManagerView && <Button label="Export Excel" icon="pi pi-file-excel" className="p-button-success p-button-sm" onClick={exportToExcel} disabled={!hasData} />}
                        {!isManagerView && <Button label="Export PDF" icon="pi pi-file-pdf" className="p-button-danger p-button-sm" onClick={exportToPDF} disabled={!hasData} />}
                    </div>
                </div>
                <div className="text-xs text-color-secondary mb-2">
                    Each cell shows <strong>Total Hourly Sales</strong> and <strong>Qty</strong>. Hover a cell to view full split details for that time slot.
                </div>
                <div className="mb-3 flex align-items-center gap-2 flex-wrap">
                    <div className="max-w-20rem" style={{ position: 'relative', minWidth: '12rem' }}>
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search" />
                            <InputText value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} placeholder="Search items..." className="w-full" style={{ paddingRight: itemSearch ? '2rem' : undefined }} />
                        </span>
                        {itemSearch && (
                            <span
                                role="button"
                                tabIndex={0}
                                style={{
                                    position: 'absolute',
                                    right: '0.75rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                    width: '1.25rem',
                                    height: '1.25rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.875rem'
                                }}
                                onClick={() => setItemSearch('')}
                                onKeyDown={(e) => e.key === 'Enter' && setItemSearch('')}
                                aria-label="Clear search"
                            >
                                <i className="pi pi-times" />
                            </span>
                        )}
                    </div>
                    {selectedItems.length > 0 && <Button label="Clear all" icon="pi pi-times-circle" onClick={() => setSelectedItems([])} size="small" style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }} />}
                </div>
                <DataTable
                    value={pivotRows}
                    dataKey={(row) => (row._isSummaryRow ? '_summary_' : row.item_name)}
                    showGridlines
                    scrollable
                    scrollDirection="both"
                    stripedRows
                    tableStyle={{ minWidth: '50rem' }}
                    className="p-datatable-sm hourly-pivot-table"
                    emptyMessage="No matching items"
                    onRowClick={(e) => {
                        if (e.data._isSummaryRow) return;
                        const name = e.data.item_name;
                        setSelectedItems((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
                    }}
                    rowClassName={(rowData) => (rowData._isSummaryRow ? 'hourly-selected-total-row' : selectedItems.includes(rowData.item_name) ? 'hourly-selected-row' : '')}
                    rowStyle={(rowData) => (rowData._isSummaryRow ? { cursor: 'default' } : { cursor: 'pointer' })}
                >
                    <Column
                        field="item_name"
                        header={itemHeaderTemplate}
                        frozen
                        style={{ minWidth: '200px', fontWeight: '600' }}
                        footer={isManagerView ? null :
                            <div
                                style={{
                                    lineHeight: 1.3,
                                    textAlign: 'center',
                                    fontWeight: 700,
                                    color: 'var(--primary-color)'
                                }}
                            >
                                <div>Total</div>
                            </div>
                        }
                        body={(row) => (row._isSummaryRow ? <span style={{ fontWeight: 700 }}>Total (Selected)</span> : <span style={{ fontWeight: 600 }}>{row.item_name}</span>)}
                    />
                    {allTimeRanges.map((tr) => (
                        <Column
                            key={tr}
                            header={tr}
                            style={{ minWidth: '150px', textAlign: 'right' }}
                            footer={isManagerView ? null :
                                grandTotalByTimeRange[tr] ? (
                                    <div
                                        style={{
                                            lineHeight: 1.3,
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            color: 'var(--primary-color)'
                                        }}
                                    >
                                        <div>{`₹${formatCurrency(grandTotalByTimeRange[tr].sales)}`}</div>
                                        <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>{`Qty: ${grandTotalByTimeRange[tr].qty}`}</div>
                                    </div>
                                ) : (
                                    ''
                                )
                            }
                            body={(row) =>
                                row._isSummaryRow ? (
                                    <div style={{ lineHeight: 1.3 }}>
                                        <div>{`₹${formatCurrency(row[tr]?.total_amount || 0)}`}</div>
                                        <div style={{ fontSize: '0.8rem' }}>{`Qty: ${row[tr]?.total_qty || 0}`}</div>
                                    </div>
                                ) : (
                                    renderTimeCell(row[tr])
                                )
                            }
                        />
                    ))}
                </DataTable>
            </Card>
        </div>
    );
};

const TaxwiseReportTab = ({ taxwiseReport, fromDate, toDate, selectedBranch, isManagerView = false }) => {
    const hasData = Array.isArray(taxwiseReport) && taxwiseReport.length > 0;

    const gstHalf = (row, expectedPct) => {
        if (parseFloat(row.tax_percentage || 0) !== expectedPct) return '';
        return `₹${formatCurrency(parseNumber(row.tax_amount) / 2)}`;
    };
    const gstHalfNum = (row, expectedPct) =>
        parseFloat(row.tax_percentage || 0) !== expectedPct ? 0 : parseNumber(row.tax_amount) / 2;

    const columnsForExport = [
        { header: 'Product',    getValue: (r) => r.product_name  || '-' },
        { header: 'Category',   getValue: (r) => r.category_name || '-' },
        { header: 'Basic Rate', getValue: (r) => parseNumber(r.basic_rate), numeric: true, skipSum: true },
        { header: 'Total Qty',  getValue: (r) => parseNumber(r.total_qty), numeric: true },
        { header: 'Total Sales',getValue: (r) => parseNumber(r.total_amount), numeric: true },
        { header: 'CGST 0%',   getValue: (r) => gstHalfNum(r, 0), numeric: true },
        { header: 'SGST 0%',   getValue: (r) => gstHalfNum(r, 0), numeric: true },
        { header: 'CGST 2.5%', getValue: (r) => gstHalfNum(r, 5), numeric: true },
        { header: 'SGST 2.5%', getValue: (r) => gstHalfNum(r, 5), numeric: true },
        { header: 'CGST 6%',   getValue: (r) => gstHalfNum(r, 12), numeric: true },
        { header: 'SGST 6%',   getValue: (r) => gstHalfNum(r, 12), numeric: true },
        { header: 'CGST 9%',   getValue: (r) => gstHalfNum(r, 18), numeric: true },
        { header: 'SGST 9%',   getValue: (r) => gstHalfNum(r, 18), numeric: true },
        { header: 'Total GST', getValue: (r) => parseNumber(r.tax_amount), numeric: true }
    ];

    const exportToExcel = async () => {
        if (!hasData) return;
        await exportStyledExcel({
            fileName: `ItemTaxwiseReport_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheetName: 'Item Taxwise Report',
            title: 'Item Taxwise Report',
            filterLines: buildReportFilterLines({ fromDate, toDate, selectedBranch }),
            columns: columnsForExport,
            rows: taxwiseReport
        });
    };

    const exportToPDF = () => {
        if (!hasData) return;
        const doc = new jsPDF('l', 'mm', 'a4');
        doc.setFontSize(14);
        doc.text('Item Tax-wise Report', 14, 15);
        doc.setFontSize(9);
        let y = 23;
        if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, y);
            y += 5;
        }
        if (selectedBranch && (selectedBranch.branch_name || selectedBranch)) {
            doc.text(`Branch: ${selectedBranch.branch_name || selectedBranch}`, 14, y);
            y += 5;
        }
        autoTable(doc, {
            startY: y + 2,
            head: [columnsForExport.map((c) => c.header)],
            body: taxwiseReport.map((row) => columnsForExport.map((col) => formatCurrency(col.getValue(row)))),
            styles: { fontSize: 7 },
            headStyles: { fillColor: [0, 122, 255] }
        });
        doc.save(`ItemTaxwiseReport_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    if (!hasData) {
        return (
            <Card>
                <EmptyState message="Tax-wise report will show here once data is loaded. Select Tax-wise tab, apply filters and click Apply." />
            </Card>
        );
    }

    return (
        <div className="bill-reports-container">
            <Card>
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <h3 className="text-xl font-semibold m-0">Tax-wise Sales</h3>
                    <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                        {(fromDate || toDate) && (
                            <span className="text-sm text-color-secondary">
                                {fromDate && toDate
                                    ? `${typeof fromDate === 'object' && fromDate.toLocaleDateString ? fromDate.toLocaleDateString('en-IN') : fromDate} – ${
                                          typeof toDate === 'object' && toDate.toLocaleDateString ? toDate.toLocaleDateString('en-IN') : toDate
                                      }`
                                    : ''}
                                {selectedBranch && (selectedBranch.branch_name || selectedBranch) && ` • ${selectedBranch.branch_name || selectedBranch}`}
                            </span>
                        )}
                        {!isManagerView && <Button label="Export Excel" icon="pi pi-file-excel" className="p-button-success p-button-sm" onClick={exportToExcel} disabled={!hasData} />}
                        {!isManagerView && <Button label="Export PDF" icon="pi pi-file-pdf" className="p-button-danger p-button-sm" onClick={exportToPDF} disabled={!hasData} />}
                    </div>
                </div>
                <DataTable value={taxwiseReport} dataKey="product_name" showGridlines paginator rows={10} rowsPerPageOptions={[10, 25, 50]} tableStyle={{ minWidth: '60rem' }} className="p-datatable-sm" scrollable scrollDirection="both">
                    <Column field="product_name"   header="Product"    sortable style={{ minWidth: '140px' }} />
                    <Column field="category_name"  header="Category"   sortable style={{ minWidth: '120px' }} />
                    <Column field="basic_rate"     header="Basic Rate" sortable body={(r) => `₹${formatCurrency(r.basic_rate)}`}   style={{ textAlign: 'right', minWidth: '100px' }} />
                    <Column field="total_qty"      header="Total Qty"  sortable style={{ textAlign: 'right', minWidth: '90px' }} />
                    <Column field="total_amount"   header="Total Sales" sortable body={(r) => `₹${formatCurrency(r.total_amount)}`} style={{ textAlign: 'right', minWidth: '110px' }} />
                    <Column header="CGST 0%"   body={(r) => gstHalf(r, 0)}  style={{ textAlign: 'right', minWidth: '90px' }} />
                    <Column header="SGST 0%"   body={(r) => gstHalf(r, 0)}  style={{ textAlign: 'right', minWidth: '90px' }} />
                    <Column header="CGST 2.5%" body={(r) => gstHalf(r, 5)}  style={{ textAlign: 'right', minWidth: '95px' }} />
                    <Column header="SGST 2.5%" body={(r) => gstHalf(r, 5)}  style={{ textAlign: 'right', minWidth: '95px' }} />
                    <Column header="CGST 6%"   body={(r) => gstHalf(r, 12)} style={{ textAlign: 'right', minWidth: '90px' }} />
                    <Column header="SGST 6%"   body={(r) => gstHalf(r, 12)} style={{ textAlign: 'right', minWidth: '90px' }} />
                    <Column header="CGST 9%"   body={(r) => gstHalf(r, 18)} style={{ textAlign: 'right', minWidth: '90px' }} />
                    <Column header="SGST 9%"   body={(r) => gstHalf(r, 18)} style={{ textAlign: 'right', minWidth: '90px' }} />
                    <Column field="tax_amount"     header="Total GST"  sortable body={(r) => `₹${formatCurrency(r.tax_amount)}`}   style={{ textAlign: 'right', minWidth: '100px' }} />
                </DataTable>
            </Card>
        </div>
    );
};

const PerformanceReportTab = ({ performanceReport, fromDate, toDate, selectedBranch, isManagerView = false }) => {
    const { top_selling = [], bottom_selling = [], report_period } = performanceReport || {};
    const hasData = (Array.isArray(top_selling) && top_selling.length > 0) || (Array.isArray(bottom_selling) && bottom_selling.length > 0);

    const performanceColumns = [
        { header: 'Product', getValue: (r) => r.product_name || '-' },
        { header: 'Total Qty', getValue: (r) => parseNumber(r.total_qty), numeric: true },
        { header: 'Total Revenue', getValue: (r) => parseNumber(r.total_amount), numeric: true }
    ];

    const exportToExcel = async () => {
        if (!hasData) return;
        const filterLines = buildReportFilterLines({ fromDate, toDate, selectedBranch });
        await exportStyledExcelMultiSheet({
            fileName: `ItemPerformanceReport_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheets: [
                { sheetName: 'Top Selling', title: 'Item Performance – Top Selling', filterLines, columns: performanceColumns, rows: top_selling },
                { sheetName: 'Bottom Selling', title: 'Item Performance – Bottom Selling', filterLines, columns: performanceColumns, rows: bottom_selling }
            ]
        });
    };

    const exportToPDF = () => {
        if (!hasData) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        doc.setFontSize(14);
        doc.text('Item Performance Ranking', 14, 15);
        doc.setFontSize(9);
        let y = 23;
        if (report_period?.from && report_period?.to) {
            doc.text(`Period: ${report_period.from} to ${report_period.to}`, 14, y);
            y += 5;
        } else if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, y);
            y += 5;
        }
        if (selectedBranch && (selectedBranch.branch_name || selectedBranch)) {
            doc.text(`Branch: ${selectedBranch.branch_name || selectedBranch}`, 14, y);
            y += 5;
        }
        doc.setFontSize(11);
        doc.text('Top Selling', 14, y + 2);
        autoTable(doc, {
            startY: y + 5,
            head: [['Product', 'Total Qty', 'Total Revenue']],
            body: top_selling.map((r) => [r.product_name || '-', parseNumber(r.total_qty).toString(), formatCurrency(r.total_amount)]),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [0, 122, 255] }
        });
        const nextY = (doc.lastAutoTable?.finalY || y + 30) + 8;
        doc.setFontSize(11);
        doc.text('Bottom Selling', 14, nextY);
        autoTable(doc, {
            startY: nextY + 3,
            head: [['Product', 'Total Qty', 'Total Revenue']],
            body: bottom_selling.map((r) => [r.product_name || '-', parseNumber(r.total_qty).toString(), formatCurrency(r.total_amount)]),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [0, 122, 255] }
        });
        doc.save(`ItemPerformanceReport_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    if (!hasData) {
        return (
            <Card>
                <EmptyState message="Performance ranking will show here once data is loaded. Select Performance Ranking tab, apply filters and click Apply." />
            </Card>
        );
    }

    return (
        <div className="bill-reports-container">
            <Card>
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <h3 className="text-xl font-semibold m-0">Performance Ranking</h3>
                    <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                        {(report_period?.from || fromDate) && (
                            <span className="text-sm text-color-secondary">
                                {report_period?.from && report_period?.to
                                    ? `${report_period.from} – ${report_period.to}`
                                    : fromDate && toDate
                                    ? `${typeof fromDate === 'object' && fromDate.toLocaleDateString ? fromDate.toLocaleDateString('en-IN') : fromDate} – ${
                                          typeof toDate === 'object' && toDate.toLocaleDateString ? toDate.toLocaleDateString('en-IN') : toDate
                                      }`
                                    : ''}
                                {selectedBranch && (selectedBranch.branch_name || selectedBranch) && ` • ${selectedBranch.branch_name || selectedBranch}`}
                            </span>
                        )}
                        {!isManagerView && <Button label="Export Excel" icon="pi pi-file-excel" className="p-button-success p-button-sm" onClick={exportToExcel} disabled={!hasData} />}
                        {!isManagerView && <Button label="Export PDF" icon="pi pi-file-pdf" className="p-button-danger p-button-sm" onClick={exportToPDF} disabled={!hasData} />}
                    </div>
                </div>
                <div className="grid">
                    <div className="col-12 lg:col-6">
                        <h4 style={{ textAlign: 'center', margin: '0 0 0.5rem 0' }}>Top Selling</h4>
                        <DataTable value={top_selling} dataKey="product_name" showGridlines paginator rows={10} className="p-datatable-sm">
                            <Column field="product_name" header="Product" sortable />
                            <Column field="total_qty" header="Total Qty" style={{ textAlign: 'right' }} />
                            <Column field="total_amount" header="Total Revenue" body={(r) => `₹${formatCurrency(r.total_amount)}`} style={{ textAlign: 'right' }} />
                        </DataTable>
                    </div>
                    <div className="col-12 lg:col-6">
                        <h4 style={{ textAlign: 'center', margin: '0 0 0.5rem 0' }}>Bottom Selling</h4>
                        <DataTable value={bottom_selling} dataKey="product_name" showGridlines paginator rows={10} className="p-datatable-sm">
                            <Column field="product_name" header="Product" sortable />
                            <Column field="total_qty" header="Total Qty" style={{ textAlign: 'right' }} />
                            <Column field="total_amount" header="Total Revenue" body={(r) => `₹${formatCurrency(r.total_amount)}`} style={{ textAlign: 'right' }} />
                        </DataTable>
                    </div>
                </div>
            </Card>
        </div>
    );
};

const ItemReportContent = ({ isManagerView = false }) => {
    const dispatch = useDispatch();
    const fullReport = useSelector(selectFullReport);
    const hourlyReport = useSelector(selectHourlyReport);
    const taxwiseReport = useSelector(selectTaxwiseReport);
    const performanceReport = useSelector(selectPerformanceReport);
    const fromDate = useSelector(selectFromDate);
    const toDate = useSelector(selectToDate);
    const selectedBranch = useSelector(selectSelectedBranch);
    const activeTab = useSelector(selectActiveTab);
    const loading = useSelector(selectActiveLoading);
    const error = useSelector(selectActiveError);

    const handleTabChange = (e) => {
        const index = e.index;
        dispatch(setActiveTab(index));
        if (fromDate && toDate) {
            const branchId = getBranchId(selectedBranch);
            if (index === ITEM_REPORT_TABS.FULL) dispatch(fetchItemFullReport({ fromDate, toDate, branchId }));
            else if (index === ITEM_REPORT_TABS.HOURLY) dispatch(fetchItemHourlyReport({ fromDate, toDate, branchId }));
            else if (index === ITEM_REPORT_TABS.TAXWISE) dispatch(fetchItemTaxwiseReport({ fromDate, toDate, branchId }));
            else if (index === ITEM_REPORT_TABS.PERFORMANCE) dispatch(fetchItemPerformanceReport({ fromDate, toDate, branchId }));
        }
    };

    const showSummaryCard = !isManagerView && fullReport?.summary && Object.keys(fullReport.summary).length > 0;

    return (
        <div className="bill-reports-container">
            {error && (
                <div
                    className="mb-3 p-3 border-round"
                    style={{
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#b91c1c'
                    }}
                >
                    <span>{error}</span>
                </div>
            )}
            {showSummaryCard && <ReportSummaryWithChart summary={fullReport.summary} fromDate={fromDate} toDate={toDate} selectedBranch={selectedBranch} title="Item Summary" subtitle="Item-wise sales" />}

            <div className="report-data-tabs">
                <TabView activeIndex={activeTab} onTabChange={handleTabChange}>
                    <TabPanel header="Full Report (Date Range)">
                        {loading ? (
                            <Card>
                                <div className="flex justify-content-center align-items-center p-6">
                                    <ProgressSpinner />
                                </div>
                            </Card>
                        ) : (
                            <FullReportTab fullReport={fullReport} fromDate={fromDate} toDate={toDate} selectedBranch={selectedBranch} isManagerView={isManagerView} />
                        )}
                    </TabPanel>
                    <TabPanel header="Hourly Sales">
                        {loading ? (
                            <Card>
                                <div className="flex justify-content-center align-items-center p-6">
                                    <ProgressSpinner />
                                </div>
                            </Card>
                        ) : (
                            <HourlyReportTab hourlyReport={hourlyReport} fromDate={fromDate} toDate={toDate} selectedBranch={selectedBranch} isManagerView={isManagerView} />
                        )}
                    </TabPanel>
                    <TabPanel header="Tax-wise Sales">
                        {loading ? (
                            <Card>
                                <div className="flex justify-content-center align-items-center p-6">
                                    <ProgressSpinner />
                                </div>
                            </Card>
                        ) : (
                            <TaxwiseReportTab taxwiseReport={taxwiseReport} fromDate={fromDate} toDate={toDate} selectedBranch={selectedBranch} isManagerView={isManagerView} />
                        )}
                    </TabPanel>
                    <TabPanel header="Performance Ranking">
                        {loading ? (
                            <Card>
                                <div className="flex justify-content-center align-items-center p-6">
                                    <ProgressSpinner />
                                </div>
                            </Card>
                        ) : (
                            <PerformanceReportTab performanceReport={performanceReport} fromDate={fromDate} toDate={toDate} selectedBranch={selectedBranch} isManagerView={isManagerView} />
                        )}
                    </TabPanel>
                </TabView>
            </div>
        </div>
    );
};

export default ItemReportContent;
